import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MetricCard } from "@/components/dashboard/MetricCard";
import { StageConversionChart } from "@/components/reports/StageConversionChart";
import { PipelineCoverageChart } from "@/components/reports/PipelineCoverageChart";
import {
  reportsService,
  ReportFilters,
  UnifiedAnalyticsData,
  AnalyticsRequest,
} from "@/services/reportsService";
import ReportsSkeleton from "@/pages/ReportsSkeleton";
import { useAuth } from "@/hooks/useAuth";
import { usePipelines } from "@/hooks/usePipelines";
import { useUsers } from "@/hooks/useUsers";
import {
  TrendingUp,
  Target,
  Calendar,
  AlertCircle,
  Clock,
  PhoneCall,
  CheckCircle2,
  Percent,
  BarChart3,
  Layers,
  User,
  Users,
  ShieldCheck,
  Repeat,
  Activity,
  PieChart,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import dayjs, { type Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { DatePicker } from "antd";
import type { RangePickerProps } from "antd/es/date-picker";

dayjs.extend(isoWeek);

type RangeValue = [Dayjs, Dayjs] | null;

const getCurrentMonthFilters = (): ReportFilters => {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  return {
    dateRange: {
      start: firstDayOfMonth.toISOString().split("T")[0],
      end: today.toISOString().split("T")[0],
    },
    userId: null,
    pipelineId: null,
    contactId: null,
    clientCompanyId: null,
  };
};

export default function Reports() {
  const [analyticsData, setAnalyticsData] =
    useState<UnifiedAnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportFilters, setReportFilters] = useState<ReportFilters>(
    getCurrentMonthFilters(),
  );

  const { company } = useAuth();
  const { pipelines, loading: pipelinesLoading } = usePipelines();
  const { users, loading: usersLoading } = useUsers();

  const getCurrencySymbol = () => {
    if ((company as any)?.currency) {
      return (company as any).currency;
    }
    try {
      const storedCompany = localStorage.getItem("company");
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        if (parsed.currency) return parsed.currency;
      }
    } catch (e) {}
    return "$";
  };
  const currencySymbol = getCurrencySymbol();

  // ─── Fetch paralelo (7 endpoints, mismo body) ───────────────────────
  const fetchAnalytics = useCallback(async () => {
    if (!company?.slug) {
      setError("No se encontró el slug de la compañía");
      return;
    }

    setLoading(true);
    setError(null);

    const request: AnalyticsRequest = {
      slug: company.slug,
      filters: {
        dateRange: {
          start: reportFilters.dateRange.start,
          end: reportFilters.dateRange.end,
        },
        pipelineId: reportFilters.pipelineId ?? null,
        userId: reportFilters.userId ?? null,
        contactId: null,
        clientCompanyId: null,
      },
      config: {
        stalledDays: 30,
        retentionMonths: 12,
      },
    };

    try {
      const [leads, pipeline, time, revenue, activity, customer, charts] =
        await Promise.allSettled([
          reportsService.getLeadsMetrics(request),
          reportsService.getPipelineMetrics(request),
          reportsService.getTimeEfficiencyMetrics(request),
          reportsService.getRevenueMetrics(request),
          reportsService.getActivityMetrics(request),
          reportsService.getCustomerMetrics(request),
          reportsService.getChartsMetrics(request),
        ]);

      // Tomar meta del primer resultado exitoso
      const firstMeta =
        (leads.status === "fulfilled" && leads.value.meta) ||
        (pipeline.status === "fulfilled" && pipeline.value.meta) ||
        (revenue.status === "fulfilled" && revenue.value.meta) ||
        null;

      const unified: UnifiedAnalyticsData = {
        meta: firstMeta ?? {
          effectiveTarget: 0,
          scope: {
            isPipelineView: false,
            isUserView: false,
            pipelineName: null,
            userName: null,
            userSalesTarget: null,
          },
          pipelines: [],
        },
        leads: leads.status === "fulfilled" ? leads.value.data : null,
        pipeline: pipeline.status === "fulfilled" ? pipeline.value.data : null,
        time: time.status === "fulfilled" ? time.value.data : null,
        revenue: revenue.status === "fulfilled" ? revenue.value.data : null,
        activity: activity.status === "fulfilled" ? activity.value.data : null,
        customer: customer.status === "fulfilled" ? customer.value.data : null,
        charts: charts.status === "fulfilled" ? charts.value.data : null,
      };

      setAnalyticsData(unified);

      // Si todos fallaron, mostrar error
      const allFailed = [
        leads,
        pipeline,
        time,
        revenue,
        activity,
        customer,
        charts,
      ].every((r) => r.status === "rejected");
      if (allFailed) {
        setError(
          "Error al cargar las métricas. Por favor, intenta nuevamente.",
        );
      }
    } catch (err) {
      console.error("Error loading analytics:", err);
      setError("Error al cargar las métricas. Por favor, intenta nuevamente.");
    } finally {
      setLoading(false);
    }
  }, [company?.slug, reportFilters]);

  useEffect(() => {
    if (company?.slug) {
      fetchAnalytics();
    }
  }, [fetchAnalytics]);

  // ─── Filtrar usuarios por pipeline (permisos de rol) ─────────────
  const availableUsers = useMemo(() => {
    if (!reportFilters.pipelineId) return users;
    const selectedPipeline = pipelines.find(
      (p) => p.id === reportFilters.pipelineId,
    );
    if (!selectedPipeline?.permissions?.length) return users;
    const allowedRoleIds = selectedPipeline.permissions
      .filter((perm) => perm.can_view)
      .map((perm) => perm.role_id);
    return users.filter((u) => {
      const userRoleId = u.rol?.id;
      return userRoleId && allowedRoleIds.includes(userRoleId);
    });
  }, [users, pipelines, reportFilters.pipelineId]);

  // ─── Handlers de filtros ─────────────────────────────────────────
  const handleFiltersChange = (newFilters: ReportFilters) => {
    setReportFilters(newFilters);
  };

  const handlePipelineChange = (value: string) => {
    handleFiltersChange({
      ...reportFilters,
      pipelineId: value === "all" ? null : value,
      userId: null, // reset user al cambiar pipeline
    });
  };

  const handleUserChange = (value: string) => {
    handleFiltersChange({
      ...reportFilters,
      userId: value === "all" ? null : value,
    });
  };

  // ─── Date range ──────────────────────────────────────────────────
  const rangeValue = useMemo<RangeValue>(() => {
    if (reportFilters.dateRange.start && reportFilters.dateRange.end) {
      return [
        dayjs(reportFilters.dateRange.start, "YYYY-MM-DD"),
        dayjs(reportFilters.dateRange.end, "YYYY-MM-DD"),
      ];
    }
    return null;
  }, [reportFilters.dateRange.start, reportFilters.dateRange.end]);

  const rangePresets = useMemo<RangePickerProps["presets"]>(() => {
    const hoy = dayjs();
    const ayer = hoy.subtract(1, "day");
    const inicioSemana = hoy.startOf("isoWeek");
    const finSemana = hoy.endOf("isoWeek");
    const inicioSemanaPasada = inicioSemana.subtract(1, "week");
    const finSemanaPasada = inicioSemana.subtract(1, "day");
    const inicioMes = hoy.startOf("month");
    const finMes = hoy.endOf("month");
    const inicioMesPasado = inicioMes.subtract(1, "month");
    const finMesPasado = inicioMes.subtract(1, "day");
    return [
      { label: "Hoy", value: [hoy, hoy] },
      { label: "Ayer", value: [ayer, ayer] },
      { label: "Esta semana", value: [inicioSemana, finSemana] },
      { label: "Semana pasada", value: [inicioSemanaPasada, finSemanaPasada] },
      { label: "Este mes", value: [inicioMes, finMes] },
      { label: "Mes pasado", value: [inicioMesPasado, finMesPasado] },
      { label: "Últimos 3 meses", value: [hoy.subtract(3, "month"), hoy] },
      { label: "Últimos 6 meses", value: [hoy.subtract(6, "month"), hoy] },
      { label: "Último año", value: [hoy.subtract(1, "year"), hoy] },
    ];
  }, []);

  const handleRangeChange: RangePickerProps["onChange"] = (dates) => {
    if (dates === null) {
      handleFiltersChange({
        ...reportFilters,
        dateRange: { start: "", end: "" },
      });
      return;
    }
    const [start, end] = dates;
    if (start && end) {
      handleFiltersChange({
        ...reportFilters,
        dateRange: {
          start: start.format("YYYY-MM-DD"),
          end: end.format("YYYY-MM-DD"),
        },
      });
    }
  };

  // ─── Meta de ventas (read-only, viene de la respuesta) ──────────
  const effectiveTarget = analyticsData?.meta?.effectiveTarget ?? 0;
  const scope = analyticsData?.meta?.scope;
  const targetLabel = useMemo(() => {
    if (scope?.isUserView && scope.userName) return `Meta de ${scope.userName}`;
    if (scope?.isPipelineView && scope.pipelineName)
      return `Meta de ${scope.pipelineName}`;
    return "Meta de Ventas";
  }, [scope]);

  const displayedTarget = useMemo(() => {
    if (scope?.isUserView && scope.userSalesTarget != null)
      return scope.userSalesTarget;
    return effectiveTarget;
  }, [scope, effectiveTarget]);

  const companyName = company?.name || "Empresa";

  // ─── Aliases para secciones ──────────────────────────────────────
  const leadsMetrics = analyticsData?.leads;
  const pipelineMetrics = analyticsData?.pipeline;
  const timeMetrics = analyticsData?.time;
  const revenueMetrics = analyticsData?.revenue;
  const activityMetrics = analyticsData?.activity;
  const customerMetrics = analyticsData?.customer;
  const chartsData = analyticsData?.charts;

  return (
    <div className="space-y-6 p-4 md:p-6 bg-slate-50/50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            Dashboard de Reportes
          </h1>
          <p className="text-slate-600">
            Centro de métricas y análisis de rendimiento de {companyName}
          </p>
        </div>
      </div>

      {/* ─── Filtros ─────────────────────────────────────────────────── */}
      <Card className="border-slate-200 bg-white shadow-sm">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Período */}
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Calendar className="h-6 w-6 text-slate-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-medium text-slate-500 mb-1">
                  Período
                </p>
                <DatePicker.RangePicker
                  value={rangeValue}
                  onChange={handleRangeChange}
                  format="YYYY-MM-DD"
                  allowClear
                  presets={rangePresets}
                  className="w-full h-9 text-sm border-slate-200 focus:border-slate-400"
                />
              </div>
            </div>

            {/* Pipeline */}
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Target className="h-6 w-6 text-slate-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-medium text-slate-500 mb-1">
                  Pipeline
                </p>
                <Select
                  value={reportFilters.pipelineId || "all"}
                  onValueChange={handlePipelineChange}
                >
                  <SelectTrigger className="w-full h-9 text-sm border-slate-200">
                    <SelectValue placeholder="Todos los pipelines" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los pipelines</SelectItem>
                    {pipelinesLoading ? (
                      <SelectItem value="loading" disabled>
                        Cargando...
                      </SelectItem>
                    ) : (
                      pipelines.map((pipeline) => (
                        <SelectItem key={pipeline.id} value={pipeline.id}>
                          {pipeline.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Usuario */}
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <User className="h-6 w-6 text-slate-600" />
              </div>
              <div className="flex-1">
                <p className="text-xs font-medium text-slate-500 mb-1">
                  Usuario
                </p>
                <Select
                  value={reportFilters.userId || "all"}
                  onValueChange={handleUserChange}
                >
                  <SelectTrigger className="w-full h-9 text-sm border-slate-200">
                    <SelectValue placeholder="Todos los usuarios" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los usuarios</SelectItem>
                    {usersLoading ? (
                      <SelectItem value="loading" disabled>
                        Cargando...
                      </SelectItem>
                    ) : (
                      availableUsers.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.nombre ||
                            `${user.first_name} ${user.last_name}`}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Meta de Ventas (solo lectura, viene de la respuesta) */}
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-lg">
                {currencySymbol}
              </div>
              <div className="flex-1">
                <p className="text-xs font-medium text-slate-500 mb-1">
                  {targetLabel}
                </p>
                <div className="h-9 flex items-center px-3 bg-slate-50 border border-slate-200 rounded-md text-sm font-semibold text-slate-700">
                  {currencySymbol}
                  {displayedTarget.toLocaleString()}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ─── Error ───────────────────────────────────────────────────── */}
      {error && (
        <Alert
          variant="destructive"
          className="animate-in fade-in duration-300"
        >
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* ─── Contenido ───────────────────────────────────────────────── */}
      {loading ? (
        <ReportsSkeleton />
      ) : (
        <div className="space-y-6 animate-in fade-in duration-300">
          {/* ═══ LEADS ═══ */}
          {leadsMetrics?.metrics ? (
            <Card className="border-slate-200 bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Target className="h-6 w-6 text-slate-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">
                      Métricas de Leads
                    </h2>
                    <p className="text-sm text-slate-500">
                      Análisis de generación y calificación de leads
                    </p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <MetricCard
                    title={leadsMetrics.metrics.volume.label}
                    value={leadsMetrics.metrics.volume.value}
                    icon={Target}
                  />
                  <MetricCard
                    title={leadsMetrics.metrics.responseTime.label}
                    value={`${leadsMetrics.metrics.responseTime.value} ${leadsMetrics.metrics.responseTime.unit}`}
                    icon={Clock}
                  />
                  <MetricCard
                    title={leadsMetrics.metrics.contactability.label}
                    value={`${leadsMetrics.metrics.contactability.value}${leadsMetrics.metrics.contactability.unit}`}
                    icon={PhoneCall}
                  />
                  <MetricCard
                    title={leadsMetrics.metrics.qualification.label}
                    value={`${leadsMetrics.metrics.qualification.value}${leadsMetrics.metrics.qualification.unit}`}
                    icon={CheckCircle2}
                  />
                  <MetricCard
                    title={leadsMetrics.metrics.conversion.label}
                    value={`${leadsMetrics.metrics.conversion.value}${leadsMetrics.metrics.conversion.unit}`}
                    icon={TrendingUp}
                  />
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-2 border-dashed border-slate-300 bg-slate-50/50">
              <CardContent className="py-16">
                <div className="text-center space-y-4">
                  <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
                    <Target className="h-8 w-8 text-slate-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      No hay datos disponibles
                    </h3>
                    <p className="text-sm text-slate-500 max-w-md mx-auto mt-2">
                      No se encontraron métricas de leads para el período
                      seleccionado
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ═══ PIPELINE ═══ */}
          {pipelineMetrics?.metrics ? (
            <Card className="border-slate-200 bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Layers className="h-6 w-6 text-slate-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">
                      Pipeline
                    </h2>
                    <p className="text-sm text-slate-500">
                      Análisis de oportunidades y embudo de ventas
                    </p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <PipelineCoverageChart
                      pipelineValue={
                        pipelineMetrics.metrics.coverage.details.pipelineValue
                      }
                      salesTarget={
                        pipelineMetrics.metrics.coverage.details.salesTarget
                      }
                      gap={pipelineMetrics.metrics.coverage.details.gap}
                    />
                    <StageConversionChart
                      data={pipelineMetrics.metrics.stageConversion.value}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <Card className="border-slate-200/60 shadow-sm">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0 space-y-1">
                            <p className="text-sm font-medium text-slate-600 truncate">
                              {pipelineMetrics.metrics.pipelineValue.label}
                            </p>
                            <p className="text-2xl font-bold text-slate-900">{`${currencySymbol}${pipelineMetrics.metrics.pipelineValue.value.toLocaleString()}`}</p>
                          </div>
                          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-slate-100 text-slate-600 font-bold text-xl flex-shrink-0">
                            {currencySymbol}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    <MetricCard
                      title={pipelineMetrics.metrics.coverage.label}
                      value={`${pipelineMetrics.metrics.coverage.value.toFixed(1)}%`}
                      icon={BarChart3}
                    />
                    <MetricCard
                      title={pipelineMetrics.metrics.winRate.label}
                      value={`${pipelineMetrics.metrics.winRate.value.toFixed(1)}%`}
                      icon={TrendingUp}
                    />
                    <MetricCard
                      title={pipelineMetrics.metrics.forecastAccuracy.label}
                      value={`${pipelineMetrics.metrics.forecastAccuracy.value.toFixed(1)}%`}
                      icon={Percent}
                    />

                    <Card className="border-slate-200/60 shadow-sm">
                      <CardContent className="p-4">
                        <div className="space-y-2.5">
                          <div className="flex items-center gap-2">
                            <div className="h-7 w-7 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center flex-shrink-0">
                              <CheckCircle2 className="h-3.5 w-3.5 text-slate-600" />
                            </div>
                            <h3 className="text-xs font-medium text-slate-500">
                              Detalles de Conversión
                            </h3>
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center py-1">
                              <span className="text-xs text-slate-500">
                                Total Cerradas
                              </span>
                              <span className="text-sm font-semibold text-slate-900">
                                {
                                  pipelineMetrics.metrics.winRate.details
                                    .totalClosed
                                }
                              </span>
                            </div>
                            <div className="h-px bg-slate-100" />
                            <div className="flex justify-between items-center py-1">
                              <span className="text-xs text-slate-500">
                                Ganadas
                              </span>
                              <span className="text-sm font-semibold text-green-600">
                                {pipelineMetrics.metrics.winRate.details.won}
                              </span>
                            </div>
                            <div className="h-px bg-slate-100" />
                            <div className="flex justify-between items-center py-1">
                              <span className="text-xs text-slate-500">
                                Perdidas
                              </span>
                              <span className="text-sm font-semibold text-red-600">
                                {pipelineMetrics.metrics.winRate.details.lost}
                              </span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-2 border-dashed border-slate-300 bg-slate-50/50">
              <CardContent className="py-16">
                <div className="text-center space-y-4">
                  <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
                    <Layers className="h-8 w-8 text-slate-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      No hay datos de pipeline disponibles
                    </h3>
                    <p className="text-sm text-slate-500 max-w-md mx-auto mt-2">
                      No se encontraron métricas de pipeline para el período
                      seleccionado.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ═══ CICLO DE VENTA (TIME) ═══ */}
          {timeMetrics?.metrics ? (
            <Card className="border-slate-200 bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Clock className="h-6 w-6 text-slate-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">
                      Ciclo de Venta
                    </h2>
                    <p className="text-sm text-slate-500">
                      Análisis de duración y tiempos en el ciclo de ventas
                    </p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Tiempo Promedio por Etapa */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <Clock className="h-5 w-5 text-slate-600" />
                          Tiempo Promedio por Etapa
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          {timeMetrics.metrics.timePerStage.value.map(
                            (stage, index) => {
                              const maxDays = Math.max(
                                ...timeMetrics.metrics.timePerStage.value.map(
                                  (s) => s.average_days,
                                ),
                              );
                              const percentage =
                                maxDays > 0
                                  ? (stage.average_days / maxDays) * 100
                                  : 0;

                              return (
                                <div key={index} className="space-y-2">
                                  <div className="flex justify-between items-center">
                                    <div className="flex-1">
                                      <h4 className="text-sm font-semibold text-slate-900">
                                        {stage.stage_name}
                                      </h4>
                                      <p className="text-xs text-slate-500">
                                        {stage.count}{" "}
                                        {stage.count === 1
                                          ? "oportunidad"
                                          : "oportunidades"}
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-lg font-bold text-purple-600">
                                        {stage.average_days.toFixed(1)}d
                                      </p>
                                    </div>
                                  </div>

                                  <div className="relative h-8 bg-gradient-to-r from-slate-100 to-slate-50 rounded-lg overflow-hidden border border-slate-200">
                                    <div
                                      className="absolute inset-y-0 left-0 bg-gradient-to-r from-purple-500 to-purple-600 transition-all duration-500 ease-out flex items-center justify-between px-3"
                                      style={{
                                        width: `${Math.min(percentage, 100)}%`,
                                      }}
                                    >
                                      <span className="text-xs font-medium text-white/90">
                                        {stage.min_days}d
                                      </span>
                                      {percentage > 50 && (
                                        <span className="text-xs font-semibold text-white">
                                          {percentage.toFixed(0)}%
                                        </span>
                                      )}
                                    </div>
                                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                      <span
                                        className={`text-xs font-medium ${percentage > 85 ? "text-white" : "text-slate-600"}`}
                                      >
                                        {stage.max_days}d
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              );
                            },
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Resumen lateral */}
                    <div className="space-y-4">
                      <MetricCard
                        title={timeMetrics.metrics.cycleDuration.label}
                        value={`${timeMetrics.metrics.cycleDuration.value} días`}
                        icon={Calendar}
                      />
                      <MetricCard
                        title={timeMetrics.metrics.stalledOpps.label}
                        value={timeMetrics.metrics.stalledOpps.value}
                        icon={AlertCircle}
                      />
                      <Card className="border-slate-200/60 shadow-sm">
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0 space-y-1">
                              <p className="text-sm font-medium text-slate-600 truncate">
                                Valor Estancado
                              </p>
                              <p className="text-2xl font-bold text-slate-900">{`${currencySymbol}${timeMetrics.metrics.stalledOpps.details.totalValue.toLocaleString()}`}</p>
                            </div>
                            <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-slate-100 text-slate-600 font-bold text-xl flex-shrink-0">
                              {currencySymbol}
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {timeMetrics.metrics.stalledOpps.details.opportunities
                        .length > 0 && (
                        <Card className="border-orange-200 bg-orange-50/50">
                          <CardContent className="pt-6">
                            <div className="flex items-center gap-2 mb-3">
                              <AlertCircle className="h-4 w-4 text-orange-600" />
                              <span className="text-sm font-semibold text-orange-900">
                                {
                                  timeMetrics.metrics.stalledOpps.details
                                    .opportunities.length
                                }{" "}
                                oportunidad(es) requieren atención
                              </span>
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </div>

                  {/* Tabla de oportunidades estancadas */}
                  {timeMetrics.metrics.stalledOpps.details.opportunities
                    .length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                          <AlertCircle className="h-5 w-5 text-orange-600" />
                          Oportunidades Estancadas - Requieren Atención
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {timeMetrics.metrics.stalledOpps.details.opportunities.map(
                            (opp) => (
                              <div
                                key={opp.id}
                                className="flex justify-between items-center p-4 bg-orange-50 rounded-lg border border-orange-200"
                              >
                                <div className="flex-1">
                                  <p className="font-medium text-slate-900">
                                    {opp.name}
                                  </p>
                                  <div className="flex gap-4 mt-2">
                                    <span className="text-xs text-slate-500">
                                      Etapa:{" "}
                                      <span className="font-medium">
                                        {opp.stage}
                                      </span>
                                    </span>
                                    <span className="text-xs text-orange-600 font-medium">
                                      {opp.days_stalled} días sin movimiento
                                    </span>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="text-lg font-bold text-slate-900">
                                    {currencySymbol}
                                    {(opp.total || 0).toLocaleString()}
                                  </p>
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-2 border-dashed border-slate-300 bg-slate-50/50">
              <CardContent className="py-16">
                <div className="text-center space-y-4">
                  <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
                    <Clock className="h-8 w-8 text-slate-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      No hay datos de tiempo disponibles
                    </h3>
                    <p className="text-sm text-slate-500 max-w-md mx-auto mt-2">
                      No se encontraron métricas de tiempo para el período
                      seleccionado.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ═══ REVENUE ═══ */}
          {revenueMetrics?.metrics ? (
            <Card className="border-slate-200 bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-lg">
                    {currencySymbol}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">
                      Ingresos
                    </h2>
                    <p className="text-sm text-slate-500">
                      Análisis de ingresos, ticket promedio y pronóstico
                    </p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="border-slate-200/60 shadow-sm">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0 space-y-1">
                          <p className="text-sm font-medium text-slate-600 truncate">
                            {revenueMetrics.metrics.totalRevenue.label}
                          </p>
                          <p className="text-2xl font-bold text-slate-900">{`${currencySymbol}${revenueMetrics.metrics.totalRevenue.value.toLocaleString()}`}</p>
                        </div>
                        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-slate-100 text-slate-600 font-bold text-xl flex-shrink-0">
                          {currencySymbol}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <MetricCard
                    title={revenueMetrics.metrics.averageTicket.label}
                    value={`${currencySymbol}${revenueMetrics.metrics.averageTicket.value.toLocaleString()}`}
                    icon={BarChart3}
                  />
                  <MetricCard
                    title={revenueMetrics.metrics.forecast.label}
                    value={`${currencySymbol}${revenueMetrics.metrics.forecast.value.toLocaleString()}`}
                    icon={TrendingUp}
                  />
                  <MetricCard
                    title={revenueMetrics.metrics.goalProgress.label}
                    value={`${revenueMetrics.metrics.goalProgress.value.toFixed(1)}%`}
                    icon={Percent}
                  />
                </div>

                {/* Detalles de progreso de meta */}
                {revenueMetrics.metrics.goalProgress.details && (
                  <div className="mt-4">
                    <Card className="border-slate-200/60">
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          <h3 className="text-sm font-semibold text-slate-700">
                            Progreso hacia la Meta
                          </h3>
                          <div className="relative h-4 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="absolute inset-y-0 left-0 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full transition-all duration-500"
                              style={{
                                width: `${Math.min(revenueMetrics.metrics.goalProgress.value, 100)}%`,
                              }}
                            />
                          </div>
                          <div className="grid grid-cols-3 gap-4 text-center">
                            <div>
                              <p className="text-xs text-slate-500">Ingresos</p>
                              <p className="text-sm font-bold text-slate-900">
                                {currencySymbol}
                                {revenueMetrics.metrics.goalProgress.details.revenue.toLocaleString()}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Meta</p>
                              <p className="text-sm font-bold text-slate-900">
                                {currencySymbol}
                                {revenueMetrics.metrics.goalProgress.details.salesTarget.toLocaleString()}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Restante</p>
                              <p className="text-sm font-bold text-orange-600">
                                {currencySymbol}
                                {revenueMetrics.metrics.goalProgress.details.remaining.toLocaleString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="border-2 border-dashed border-slate-300 bg-slate-50/50">
              <CardContent className="py-16">
                <div className="text-center space-y-4">
                  <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
                    <div className="text-3xl font-bold text-slate-400">
                      {currencySymbol}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      No hay datos de ingresos disponibles
                    </h3>
                    <p className="text-sm text-slate-500 max-w-md mx-auto mt-2">
                      No se encontraron métricas de ingresos para el período
                      seleccionado.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ═══ ACTIVIDAD ═══ */}
          {activityMetrics?.metrics ? (
            <Card className="border-slate-200 bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <Activity className="h-6 w-6 text-slate-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">
                      Actividad
                    </h2>
                    <p className="text-sm text-slate-500">
                      Métricas de actividad de vendedores
                    </p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <MetricCard
                    title={activityMetrics.metrics.activitiesPerSeller.label}
                    value={activityMetrics.metrics.activitiesPerSeller.value}
                    icon={Users}
                  />
                  <MetricCard
                    title={activityMetrics.metrics.activityRatio.label}
                    value={activityMetrics.metrics.activityRatio.value}
                    icon={Activity}
                  />
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-2 border-dashed border-slate-300 bg-slate-50/50">
              <CardContent className="py-16">
                <div className="text-center space-y-4">
                  <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
                    <Activity className="h-8 w-8 text-slate-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      No hay datos de actividad disponibles
                    </h3>
                    <p className="text-sm text-slate-500 max-w-md mx-auto mt-2">
                      No se encontraron métricas de actividad para el período
                      seleccionado.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ═══ CLIENTES ═══ */}
          {customerMetrics?.metrics ? (
            <Card className="border-slate-200 bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <ShieldCheck className="h-6 w-6 text-slate-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">
                      Clientes
                    </h2>
                    <p className="text-sm text-slate-500">
                      Retención, churn y valor de vida del cliente
                    </p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <MetricCard
                    title={customerMetrics.metrics.retention.label}
                    value={`${customerMetrics.metrics.retention.value}%`}
                    icon={Repeat}
                  />
                  <MetricCard
                    title={customerMetrics.metrics.churn.label}
                    value={`${customerMetrics.metrics.churn.value}%`}
                    icon={AlertCircle}
                  />
                  <Card className="border-slate-200/60 shadow-sm">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0 space-y-1">
                          <p className="text-sm font-medium text-slate-600 truncate">
                            {customerMetrics.metrics.clv.label}
                          </p>
                          <p className="text-2xl font-bold text-slate-900">{`${currencySymbol}${customerMetrics.metrics.clv.value.toLocaleString()}`}</p>
                        </div>
                        <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-slate-100 text-slate-600 font-bold text-xl flex-shrink-0">
                          {currencySymbol}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Detalles de retención */}
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card className="border-slate-200/60">
                    <CardContent className="p-4">
                      <h3 className="text-sm font-semibold text-slate-700 mb-3">
                        Detalles de Retención
                      </h3>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center py-1">
                          <span className="text-xs text-slate-500">
                            Total Clientes
                          </span>
                          <span className="text-sm font-semibold text-slate-900">
                            {
                              customerMetrics.metrics.retention.details
                                .total_clients
                            }
                          </span>
                        </div>
                        <div className="h-px bg-slate-100" />
                        <div className="flex justify-between items-center py-1">
                          <span className="text-xs text-slate-500">
                            Clientes Recurrentes
                          </span>
                          <span className="text-sm font-semibold text-green-600">
                            {
                              customerMetrics.metrics.retention.details
                                .repeat_clients
                            }
                          </span>
                        </div>
                        <div className="h-px bg-slate-100" />
                        <div className="flex justify-between items-center py-1">
                          <span className="text-xs text-slate-500">
                            Clientes Nuevos
                          </span>
                          <span className="text-sm font-semibold text-blue-600">
                            {
                              customerMetrics.metrics.retention.details
                                .new_clients
                            }
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {customerMetrics.metrics.clv.details && (
                    <Card className="border-slate-200/60">
                      <CardContent className="p-4">
                        <h3 className="text-sm font-semibold text-slate-700 mb-3">
                          Detalles CLV
                        </h3>
                        <div className="space-y-2">
                          <div className="flex justify-between items-center py-1">
                            <span className="text-xs text-slate-500">
                              Ticket Promedio
                            </span>
                            <span className="text-sm font-semibold text-slate-900">
                              {currencySymbol}
                              {customerMetrics.metrics.clv.details.average_ticket.toLocaleString()}
                            </span>
                          </div>
                          <div className="h-px bg-slate-100" />
                          <div className="flex justify-between items-center py-1">
                            <span className="text-xs text-slate-500">
                              Frecuencia de Compra
                            </span>
                            <span className="text-sm font-semibold text-slate-900">
                              {
                                customerMetrics.metrics.clv.details
                                  .purchase_frequency
                              }
                            </span>
                          </div>
                          <div className="h-px bg-slate-100" />
                          <div className="flex justify-between items-center py-1">
                            <span className="text-xs text-slate-500">
                              Meses de Retención
                            </span>
                            <span className="text-sm font-semibold text-slate-900">
                              {
                                customerMetrics.metrics.clv.details
                                  .retention_months
                              }
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-2 border-dashed border-slate-300 bg-slate-50/50">
              <CardContent className="py-16">
                <div className="text-center space-y-4">
                  <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-slate-100">
                    <ShieldCheck className="h-8 w-8 text-slate-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">
                      No hay datos de clientes disponibles
                    </h3>
                    <p className="text-sm text-slate-500 max-w-md mx-auto mt-2">
                      No se encontraron métricas de clientes para el período
                      seleccionado.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ═══ CHARTS ═══ */}
          {chartsData?.charts && (
            <Card className="border-slate-200 bg-white shadow-sm">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center">
                    <PieChart className="h-6 w-6 text-slate-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">
                      Gráficos
                    </h2>
                    <p className="text-sm text-slate-500">
                      Visualización de tendencias, embudo y motivos de pérdida
                    </p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Pipeline por Etapa (Funnel) */}
                  {chartsData.charts.pipelineByStage?.data?.length > 0 && (
                    <Card className="border-slate-200/60">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-semibold text-slate-700">
                          {chartsData.charts.pipelineByStage.label}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {chartsData.charts.pipelineByStage.data.map(
                            (stage, idx) => {
                              const maxCount = Math.max(
                                ...chartsData.charts.pipelineByStage.data.map(
                                  (s) => s.count,
                                ),
                              );
                              const pct =
                                maxCount > 0
                                  ? (stage.count / maxCount) * 100
                                  : 0;
                              return (
                                <div key={idx} className="space-y-1">
                                  <div className="flex justify-between text-xs">
                                    <span className="font-medium text-slate-700">
                                      {stage.stage_name}
                                    </span>
                                    <span className="text-slate-500">
                                      {stage.count} ops &middot; $
                                      {currencySymbol}
                                      {stage.value.toLocaleString()}
                                    </span>
                                  </div>
                                  <div className="h-6 bg-slate-100 rounded overflow-hidden">
                                    <div
                                      className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600 rounded transition-all duration-500"
                                      style={{ width: `${pct}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            },
                          )}
                          <div className="flex justify-between text-xs text-slate-500 pt-2 border-t border-slate-100">
                            <span>
                              Total:{" "}
                              {
                                chartsData.charts.pipelineByStage.summary
                                  .total_opportunities
                              }{" "}
                              oportunidades
                            </span>
                            <span>
                              {currencySymbol}
                              {chartsData.charts.pipelineByStage.summary.total_value.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Tendencia de Ventas */}
                  {chartsData.charts.salesTrend?.data?.length > 0 && (
                    <Card className="border-slate-200/60">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-semibold text-slate-700">
                          {chartsData.charts.salesTrend.label}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {chartsData.charts.salesTrend.data.map(
                            (item, idx) => (
                              <div
                                key={idx}
                                className="flex justify-between items-center p-3 bg-slate-50 rounded-lg border border-slate-100"
                              >
                                <span className="text-sm font-medium text-slate-700">
                                  {item.month}
                                </span>
                                <div className="text-right">
                                  <p className="text-sm font-bold text-slate-900">
                                    {currencySymbol}
                                    {item.value.toLocaleString()}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {item.count} ventas
                                  </p>
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Motivos de Pérdida */}
                  {chartsData.charts.lossReasons?.data?.length > 0 && (
                    <Card className="border-slate-200/60 lg:col-span-2">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-semibold text-slate-700">
                          {chartsData.charts.lossReasons.label}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {chartsData.charts.lossReasons.data.map(
                            (reason, idx) => (
                              <div
                                key={idx}
                                className="flex justify-between items-center p-3 bg-red-50 rounded-lg border border-red-100"
                              >
                                <div>
                                  <p className="text-sm font-medium text-slate-900">
                                    {reason.reason}
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {reason.count} oportunidades &middot;{" "}
                                    {reason.percentage.toFixed(1)}%
                                  </p>
                                </div>
                                <p className="text-sm font-bold text-red-600">
                                  {currencySymbol}
                                  {reason.value.toLocaleString()}
                                </p>
                              </div>
                            ),
                          )}
                          <div className="flex justify-between text-xs text-slate-500 pt-2 border-t border-slate-100">
                            <span>
                              Total perdidas:{" "}
                              {chartsData.charts.lossReasons.summary.total_lost}
                            </span>
                            <span>
                              {currencySymbol}
                              {chartsData.charts.lossReasons.summary.total_value_lost.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
